import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useHttpStore } from '@bonusly/http-client';
import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import type { BizyOwnedObservedMomentResponse } from '../../../lib/bizy/types';
import { bizyOwnedMomentPath } from '../../../queries/bizy-people';
import { headers } from '../../../queries/meta';
import { buildRecognizeMomentChatPayload } from '../lib/build-recognize-moment-chat-payload';
import { resolvePromptLibraryKey } from '../lib/prompt-library';
import { useBizyExternalOpenStore } from '../store/use-bizy-external-open-store';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { useBizyPendingChatMessageStore } from '../store/use-bizy-pending-chat-message-store';
import { BIZY_DEEP_LINK_TRIGGER, useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

// The `bizy` open/fullscreen/noticed param is owned by useBizyRouteSync (steady-state
// `?bizy=`); this hook only owns one-shot params that are stripped after consume.
export const BIZY_DEEP_LINK_PROMPT_PARAM = 'bizy_prompt';
export const BIZY_DEEP_LINK_PROMPT_KEY_PARAM = 'bizy_prompt_key';
export const BIZY_DEEP_LINK_PROMPT_MODE_PARAM = 'bizy_prompt_mode';
export const BIZY_DEEP_LINK_CONVERSATION_PARAM = 'bizy_conversation';
/** Email "Let's dive in": pair with `?bizy=chat` (route sync) + this one-shot id. */
export const BIZY_DEEP_LINK_MOMENT_PARAM = 'bizy_moment';

// Params this hook consumes and strips. NOT the `bizy` param — useBizyRouteSync
// owns that, so stripping it here would fight the sync.
const DEEP_LINK_PARAMS = [
	BIZY_DEEP_LINK_PROMPT_PARAM,
	BIZY_DEEP_LINK_PROMPT_KEY_PARAM,
	BIZY_DEEP_LINK_PROMPT_MODE_PARAM,
	BIZY_DEEP_LINK_CONVERSATION_PARAM,
	BIZY_DEEP_LINK_MOMENT_PARAM,
] as const;

// Cap the auto-sent prompt: a deep link sends this to the LLM on the user's
// behalf, so keep email/campaign links from injecting an unbounded payload.
const MAX_PROMPT_LENGTH = 2000;

export interface BizyDeepLinkIntent {
	open: boolean;
	/** Free-text prompt (`?bizy_prompt=`). Takes precedence over promptKey. */
	prompt?: string;
	/**
	 * Curated prompt reference (`?bizy_prompt_key=`): a library-relative key like
	 * `prepFollowUp.needsAgenda` (the part under `promptLibrary.library.`).
	 * Resolved + auto-translated at consume time, so the URL stays short and
	 * locale-correct. Limited to the curated `library` subtree
	 */
	promptKey?: string;
	promptMode: 'send' | 'prefill';
	conversationId?: string;
	/** Owned observed moment id (`?bizy_moment=`) — email Dive In. */
	observedMomentId?: string;
}

export function parseBizyDeepLink(search: string): BizyDeepLinkIntent | undefined {
	const params = new URLSearchParams(search);
	const rawPrompt = params.get(BIZY_DEEP_LINK_PROMPT_PARAM)?.trim();
	const promptKey = params.get(BIZY_DEEP_LINK_PROMPT_KEY_PARAM)?.trim() || undefined;
	const conversationId = params.get(BIZY_DEEP_LINK_CONVERSATION_PARAM)?.trim() || undefined;
	const observedMomentId = params.get(BIZY_DEEP_LINK_MOMENT_PARAM)?.trim() || undefined;
	const prompt = rawPrompt ? rawPrompt.slice(0, MAX_PROMPT_LENGTH) : undefined;

	// A prompt/conversation/moment implies opening the panel. A bare `?bizy=open` /
	// `?bizy=fullscreen` / `?bizy=noticed` is handled by useBizyRouteSync (+ the
	// noticed companion hook), not here.
	if (!prompt && !promptKey && !conversationId && !observedMomentId) return undefined;

	const promptMode = params.get(BIZY_DEEP_LINK_PROMPT_MODE_PARAM) === 'prefill' ? 'prefill' : 'send';

	return {
		open: true,
		prompt,
		promptKey,
		promptMode,
		conversationId,
		observedMomentId,
	};
}

function stripDeepLinkParams() {
	const url = new URL(window.location.href);
	let changed = false;
	for (const param of DEEP_LINK_PARAMS) {
		if (url.searchParams.has(param)) {
			url.searchParams.delete(param);
			changed = true;
		}
	}
	if (changed) window.history.replaceState(window.history.state, '', url);
}

function releaseDeepLinkTrigger() {
	// The deep-link trigger makes close('auto') and the fullscreen-drop effects
	// no-op so the same-mount auto-open/close effects can't undo this open before it
	// lands. Release it on the next macrotask so the fullscreen-drop resumes instead
	// of staying disabled for the rest of the session.
	// A user interaction in the meantime overwrites lastTrigger, so we only release if it's still ours.
	setTimeout(() => {
		if (useBizySidePanelStore.getState().lastTrigger === BIZY_DEEP_LINK_TRIGGER) {
			useBizySidePanelStore.setState({ lastTrigger: 'auto' });
		}
	}, 0);
}

/**
 * Deep link into Bizy chat from any URL (RR-171), mirroring
 * {@link useBizySettingsDeepLink}. Supported params, all composable:
 *
 * (Opening the panel itself is via the `?bizy=` param, owned by useBizyRouteSync.)
 * - `?bizy_prompt=<text>` — starter message
 * - `?bizy_prompt_mode=send|prefill` — auto-send (default) vs drop into the input
 * - `?bizy_conversation=<id>` — load an existing conversation; combinable with a
 *   prompt to add a follow-up into it
 * - `?bizy_moment=<observedMomentId>` — email Dive In: fetch the owned moment and
 *   queue the same chat payload as the People Browser button (takes precedence
 *   over prompt/conversation when present)
 *
 * Opens the panel, queues work through the external-open pipeline or the pending
 * chat-message store (moment dive-in), then strips the one-shot params via
 * history.replaceState so refreshes and back/forward don't re-trigger. Backend
 * authorization keeps conversation/moment ids from exposing other users' data.
 *
 * Mount this LAST among the Bizy layout effects so its open wins over the
 * mount-time auto-open/close and drop-to-side-panel effects.
 * (`useBizyMomentsDeepLink` must run before this when both could apply — it
 * selects a person for `?bizy=noticed` and must not be cleared by `showChat`.)
 */
export function useBizyDeepLink() {
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const { t } = useTranslation('admin_bizy');
	const completedMomentIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!isBizyEnabledForUser) return;

		const intent = parseBizyDeepLink(window.location.search);
		if (!intent) return;

		// Email Dive In: async fetch — do not mark consumed / strip until done
		// (Strict Mode remounts cancel the first effect; an early flag would skip
		// the remount and never send).
		if (intent.observedMomentId) {
			const momentId = intent.observedMomentId;
			if (completedMomentIdRef.current === momentId) return;

			// Same namespace-ready guard as bizy_prompt_key: bail WITHOUT stripping so
			// a later `t` update re-runs this effect instead of sending raw keys.
			const recognizeProbe = t('personView.letsRecognize', { name: 'x' });
			const recognizeForProbe = t('personView.letsRecognizeFor', { name: 'x', moment: 'y' });
			if (!recognizeProbe || recognizeProbe === 'personView.letsRecognize' || !recognizeForProbe || recognizeForProbe === 'personView.letsRecognizeFor') {
				return;
			}

			let cancelled = false;
			const store = useBizySidePanelStore.getState();
			store.showChat();
			store.open(BIZY_DEEP_LINK_TRIGGER);

			void (async () => {
				// Resolve at fetch time so mounts without a moment param (layout tests)
				// do not need HttpClientProvider just to open Bizy.
				const http = useHttpStore.getState().v2;
				if (!http) {
					if (!cancelled) {
						completedMomentIdRef.current = momentId;
						stripDeepLinkParams();
						releaseDeepLinkTrigger();
					}
					return;
				}

				try {
					const res = await http.get<BizyOwnedObservedMomentResponse>(bizyOwnedMomentPath(momentId), { headers });
					if (cancelled) return;

					const { person, moment } = res.data;
					const fallbackName = t('personView.unknownPerson');
					if (!person.name?.trim() && (!fallbackName || fallbackName === 'personView.unknownPerson')) {
						// Namespace regressed mid-fetch — leave params for retry.
						return;
					}
					const name = person.name?.trim() || fallbackName;
					useBizyPendingChatMessageStore.getState().sendChatMessage(
						buildRecognizeMomentChatPayload({
							name,
							title: moment.headline,
							body: moment.moment,
							source: moment.source,
							personId: person.id,
							avatarUrl: person.avatarUrl,
							observedMomentId: moment.id,
							t,
						})
					);
				} catch {
					// Open chat without sending — cold link may be expired/unauthorized.
				}

				if (cancelled) return;

				completedMomentIdRef.current = momentId;
				stripDeepLinkParams();
				releaseDeepLinkTrigger();
			})();

			return () => {
				cancelled = true;
			};
		}

		// Free text wins; otherwise resolve a curated prompt key to translated text.
		let starterMessage = intent.prompt;
		if (!starterMessage && intent.promptKey) {
			const key = resolvePromptLibraryKey(intent.promptKey);
			if (key) {
				const text = t(key);
				// `t` returns the key unchanged when the namespace hasn't loaded
				// yet. Bail WITHOUT stripping params so a later `t` update re-runs
				// this effect and resolves the prompt rather than losing it.
				if (!text || text === key) return;
				starterMessage = text;
			}
			// Unknown key: fall through — open the panel, drop the param.
		}

		if (starterMessage || intent.conversationId) {
			useBizyExternalOpenStore.getState().setPendingRequest({
				source: 'url_deep_link',
				pagePath: window.location.pathname,
				pageContext: useBizyPageContextStore.getState().context.pageType,
				...(starterMessage ? { starterMessage } : {}),
				...(intent.conversationId ? { conversationId: intent.conversationId } : {}),
				autoSend: intent.promptMode === 'send',
				// A bare prompt is a new topic — start fresh, no dialog.
				// `?bizy_conversation=` targets a specific conversation instead.
				forceNewConversation: Boolean(starterMessage) && !intent.conversationId,
			});
		}

		const store = useBizySidePanelStore.getState();
		store.showChat();
		store.open(BIZY_DEEP_LINK_TRIGGER);

		stripDeepLinkParams();
		releaseDeepLinkTrigger();
	}, [isBizyEnabledForUser, t]);
}
